import upperFirst from 'lodash/upperFirst';

import {
  Button,
  Icons,
  PreviewMarkdown,
  PreviewMarkdownCodeElement,
  type CodeElementProps,
  notify,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import type { FollowUp } from '@cast/types';

const PreviewMarkdownCode = (props: CodeElementProps) => (
  <PreviewMarkdownCodeElement
    {...props}
    onCopy={() => notify.success('Copied to clipboard')}
  />
);

import { trackOpsPilot } from './_analytics';

const MAX_FOLLOW_UPS = 3;

type FollowUpQuestionsProps = {
  followUps: FollowUp[];
  onFollowUpClick?: (command: string) => void;
};

export const FollowUpQuestions = ({
  followUps,
  onFollowUpClick,
}: FollowUpQuestionsProps) => {
  const visibleFollowUps = followUps.slice(0, MAX_FOLLOW_UPS);

  return (
    <Stack data-testid="follow-up-questions" mt={16} gap={8}>
      <Typography variant="P14B" color={tokens.theme.fg.structural.primary}>
        You might also ask
      </Typography>
      <Stack>
        {visibleFollowUps.map(({ command }, idx) => {
          const question = upperFirst(command);

          return (
            <Button
              key={idx}
              data-testid={`follow-up-question-${idx}`}
              variant="ghost"
              onClick={() => {
                trackOpsPilot.followUpClicked({
                  command: command,
                  position: idx + 1,
                });
                onFollowUpClick?.(question);
              }}
              sx={{
                all: 'unset',
                textWrapMode: 'wrap',
                px: 12,
                py: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                justifyContent: 'flex-start',
                alignSelf: 'stretch',
                color: tokens.theme.fg.structural.primary,
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: tokens.theme.border.focus,
                  outlineOffset: 2,
                },
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: tokens.theme.bg.structural.surfaceRaised,
                },
              }}
            >
              <Icons.ArrowBendDownRight
                weight="regular"
                size={20}
                color={tokens.theme.fg.structural.secondary}
                style={{ flexShrink: 0 }}
              />
              <PreviewMarkdown
                markdown={question}
                components={{
                  code: PreviewMarkdownCode,
                  h1: ({ children }) => <strong>{children}</strong>,
                  h2: ({ children }) => <strong>{children}</strong>,
                  h3: ({ children }) => <strong>{children}</strong>,
                  h4: ({ children }) => <strong>{children}</strong>,
                  h5: ({ children }) => <strong>{children}</strong>,
                  p: ({ children }) => (
                    <Typography
                      variant="P14R"
                      color={tokens.theme.fg.structural.primary}
                    >
                      {children}
                    </Typography>
                  ),
                }}
              />
            </Button>
          );
        })}
      </Stack>
    </Stack>
  );
};
